/**
 * Universal file-preview kind classifier (2026-08-01 Miles directive: every deliverable
 * type the platform's tiers produce must open in a user-friendly preview popup with the
 * right per-type controls; 2026-08-02 amendment: office documents render READABLY, not
 * as download cards — deep-research ships PDF+DOCX and both must be easily accessible).
 * Pure module — the overlay dispatches renderers on this and the contract is pinned by
 * tests/tools/filePreviewKind.test.ts.
 *
 * Kind → renderer essentials:
 *   image   → zoom / pan / rotate / download            (tier-8 image generation)
 *   video   → native playback controls / download       (tier-9 video generation)
 *   audio   → native playback controls / download       (tier-10 voice)
 *   pdf     → browser-native paged viewer via iframe    (vm-pdf, tier-11 documents)
 *   docx    → mammoth docx→HTML, sandboxed iframe       (deep research / tier-11)
 *   sheet   → SheetJS workbook→HTML tables, sandboxed   (xlsx/xls deliverables)
 *   scene3d → sandboxed INTERACTIVE iframe (the scene carries its own orbit/rotate
 *             controls — tier-21 contract) / fullscreen / open-in-tab / download
 *   html    → sandboxed STATIC iframe (scripts disabled — an arbitrary HTML deliverable
 *             never executes in the app origin) / download
 *   text    → scrollable mono view, copy + wrap toggle  (code, json, csv, md, logs)
 *   binary  → metadata card + download CTA              (pptx/zip/legacy-doc/unknown)
 */

export type PreviewKind =
  | 'image' | 'video' | 'audio' | 'pdf' | 'docx' | 'sheet' | 'scene3d' | 'html' | 'text' | 'binary';

const SCENE_NAME_RE = /\.(3d|scene)\.html?$/i;
const IMAGE_EXT_RE = /\.(png|jpe?g|gif|webp|avif|bmp|svg)$/i;
const VIDEO_EXT_RE = /\.(mp4|webm|mov|m4v|ogv)$/i;
const AUDIO_EXT_RE = /\.(mp3|wav|ogg|oga|m4a|flac|aac)$/i;
const TEXT_EXT_RE = /\.(txt|md|markdown|json|csv|tsv|log|xml|ya?ml|toml|ini|py|js|jsx|ts|tsx|mjs|cjs|css|scss|sh|bash|sql|glsl|frag|vert|scad|gd|cs|c|h|cpp|hpp|rs|go|java|rb|php)$/i;
const HTML_EXT_RE = /\.html?$/i;

const DOCX_MIME = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
const XLSX_MIME = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
const XLS_MIME = 'application/vnd.ms-excel';

export function resolvePreviewKind(name: string | undefined, mimeType: string | undefined): PreviewKind {
  const n = String(name || '');
  const t = String(mimeType || '').toLowerCase().split(';')[0].trim();

  // The 3D-scene naming contract wins over the generic HTML type (same rule as the
  // server-side detector in threeDPreviewDeliverable.ts — keep in lockstep).
  if (SCENE_NAME_RE.test(n)) return 'scene3d';

  if (t.startsWith('image/')) return 'image';
  if (t.startsWith('video/')) return 'video';
  if (t.startsWith('audio/')) return 'audio';
  if (t === 'application/pdf') return 'pdf';
  if (t === DOCX_MIME) return 'docx';
  if (t === XLSX_MIME || t === XLS_MIME) return 'sheet';
  if (t === 'text/html' || t === 'application/xhtml+xml') return 'html';
  if (t.startsWith('text/') || t === 'application/json' || t === 'application/xml') return 'text';

  // Untyped attachments fall back to the extension.
  if (t === '' || t === 'application/octet-stream') {
    if (IMAGE_EXT_RE.test(n)) return 'image';
    if (VIDEO_EXT_RE.test(n)) return 'video';
    if (AUDIO_EXT_RE.test(n)) return 'audio';
    if (/\.pdf$/i.test(n)) return 'pdf';
    if (/\.docx$/i.test(n)) return 'docx';
    if (/\.(xlsx|xls)$/i.test(n)) return 'sheet';
    if (HTML_EXT_RE.test(n)) return 'html';
    if (TEXT_EXT_RE.test(n)) return 'text';
  }
  return 'binary';
}

/** Human label for the preview header + the attachment-card hint. */
export function previewKindLabel(kind: PreviewKind): string {
  switch (kind) {
    case 'image': return 'Image';
    case 'video': return 'Video';
    case 'audio': return 'Audio';
    case 'pdf': return 'PDF document';
    case 'docx': return 'Word document';
    case 'sheet': return 'Spreadsheet';
    case 'scene3d': return 'Interactive 3D model';
    case 'html': return 'Web page';
    case 'text': return 'Text';
    default: return 'File';
  }
}

/** Kinds the overlay can render in-place (everything except opaque binaries). */
export function isRenderableKind(kind: PreviewKind): boolean {
  return kind !== 'binary';
}
